import { LinkComponent } from './elements/link';
import React, {
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormProvider,
  KeepStateOptions,
  useForm,
  UseFormReturn,
} from 'react-hook-form';
import yup from './yup-extended';
import { yupResolver } from '@hookform/resolvers/yup';
import { faLongArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import css from './form.module.css';
import buttonCss from './elements/button.module.css';
import Notice from '../common/notice';
import Notices from '../common/notices';
import { Observable, of } from 'rxjs';
import { addNotice, loadingFragments } from '../../store/global-slice';
import { useDispatch } from 'react-redux';
import { finalize } from 'rxjs/operators';
import Spinner from '../common/spinner';
import { pascalCase } from 'pascal-case';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import OptionModal from '../modals/option-modal';
import cn from 'classnames';

export type FormComponentProps = {
  parentName?: string;
  formValues?: any;
  readonly?: boolean;
  disabled?: boolean;
  required?: string[];
  loadingFragment?: string;
  placeholder?: string;
  horizontal?: boolean;
  className?: string;
};
export type FormComponent = FunctionComponent<FormComponentProps>;

export type SubmitType<T, T1 = T> = (
  data: T
) => [Observable<T1>, ((response: T1) => void)?];

export type ValueType = any | [any, KeepStateOptions];

export type ConfirmationModalProps = {
  title?: string;
  content: JSX.Element;
  buttons?: { title: string; name: string; value: boolean }[];
};

export default function Form({
  title,
  backTo,
  edit,
  body,
  footer,
  submit = null,
  confirmation = null,
  schema,
  values,
  noticeAccessor = null,
  mode,
  className,
  hideHeader = false,
  formMethods = null,
  header = null,
  top,
  children,
  optionModalClass,
}: {
  title: string;
  backTo?: string;
  edit?: string;
  body: FormComponent[];
  footer?: FormComponent[];
  submit?: SubmitType<any>;
  confirmation?: ConfirmationModalProps;
  schema?: yup.AnyObjectSchema;
  mode: 'view' | 'step' | 'create' | 'edit';
  values?: any | [any, KeepStateOptions];
  noticeAccessor?: string;
  className?: string;
  hideHeader?: boolean;
  formMethods?: UseFormReturn;
  header?: () => React.JSX.Element;
  top?: FormComponent[];
  children?: ReactNode | undefined;
  optionModalClass?: string;
}) {
  const { t } = useTranslation();
  const [showErrorNotice, setShowErrorNotice] = useState(false);
  const [form, onSubmit] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const dispatch = useDispatch();
  const loadingFragment = `form.${mode}.submit`;

  let innerFormMethods = useForm({
    resolver: schema ? yupResolver(schema) : null,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  if (formMethods) {
    innerFormMethods = formMethods;
  }

  const {
    handleSubmit,
    reset,
    trigger,
    setError,
    getValues,
    formState: { errors },
    setValue,
  } = innerFormMethods;

  if (process.env.NODE_ENV === 'development') {
    (window as any).errors = errors;
    (window as any).getValues = getValues;
    (window as any).trigger = trigger;
    (window as any).setValue = setValue;
  }

  const error = useCallback(() => {
    setShowErrorNotice(true);
  }, [setShowErrorNotice]);

  const confirm = useCallback(
    (form) => {
      if (confirmation) {
        setConfirmationModal(
          <OptionModal
            className={optionModalClass}
            title={confirmation.title ?? null}
            content={confirmation.content}
            buttons={
              confirmation.buttons
                ? confirmation.buttons
                : [
                    { title: t('yes'), name: 'yes', value: true },
                    { title: t('cancel'), name: 'cancel', value: false },
                  ]
            }
            onChoice={(choice) => {
              setConfirmationModal(null);
              return choice === true ? onSubmit(form) : [of(null)];
            }}
            onClose={() => setConfirmationModal(null)}
          />
        );
      } else {
        return onSubmit(form);
      }
    },
    [confirmation, optionModalClass, t]
  );

  useEffect(() => {
    let value, stateOptions;

    if (Array.isArray(values)) {
      [value, stateOptions] = values;
    } else {
      value = values;
    }
    if (value && stateOptions) {
      reset(value, stateOptions);
    } else if (value) {
      reset(value);
    }
  }, [values, reset, trigger]);

  useEffect(() => {
    if (mode === 'edit' && values) {
      trigger();
    }
  }, [values, mode, trigger]);

  useEffect(() => {
    if (form && submit) {
      const [obs, callback] = submit(form);

      dispatch(loadingFragments({ [loadingFragment]: true }));
      const subscription = obs
        .pipe(
          finalize(() => {
            dispatch(loadingFragments({ [loadingFragment]: false }));
          })
        )
        .subscribe(
          (response: any) => {
            if (mode !== 'step') {
              dispatch(
                addNotice({
                  accessor: 'ViewForm',
                  status: 'success',
                  description: t(`form.${mode}.submitted`),
                })
              );
            }

            if (callback) {
              callback(response);
            }
          },
          (errorResponse) => {
            if (errorResponse.status === 422) {
              const errors = errorResponse.response.errors;

              for (let [key, value] of Object.entries(errors)) {
                (value as string[]).forEach((v: string) =>
                  setError(key, {
                    type: 'manual',
                    message: t(v),
                  })
                );
              }
            }

            error();
          }
        );

      return () => subscription.unsubscribe();
    }
  }, [setError, form, mode, submit, loadingFragment, dispatch, error, t]);

  function requiredFields(): string[] {
    if (!schema) {
      return [];
    } else {
      return getRequiredFields(schema);
    }
  }

  function getRequiredFields(schema: any, prefix: string = ''): any {
    let fields: string[] = [];

    Object.keys(schema.fields).forEach((key) => {
      if (schema.fields[key].hasOwnProperty('fields')) {
        let subFields = getRequiredFields(schema.fields[key], key);

        fields = fields.concat(subFields);
      } else {
        const values = getValues();

        let field = schema.fields[key];

        if (field.type === 'lazy') {
          field = field.builder(values[key], { parent: values });
        }

        if (field.type === 'array' && values[key]) {
          Object.keys(values[key]).forEach((valueKey) => {
            const index = key.concat('.', valueKey);

            const subSchema = yup.reach(schema, index);

            if (subSchema.hasOwnProperty('fields')) {
              Object.keys(subSchema.fields).forEach((subKey) => {
                const fieldKey = key.concat('.', valueKey, '.', subKey);
                if (subSchema.fields[subKey]?.exclusiveTests?.required) {
                  fields.push(fieldKey);
                }

                const nextSchema = yup.reach(subSchema, subKey);
                if (nextSchema?.innerType?.hasOwnProperty('fields')) {
                  const nextValues = _.get(values, fieldKey);
                  if (nextValues) {
                    Object.keys(nextValues)?.forEach((nextValueKey) => {
                      const path = fieldKey.concat('.', nextValueKey);
                      fields = fields.concat(
                        getRequiredFields(nextSchema?.innerType).map(
                          (e: string) => path.concat('.', e)
                        )
                      );
                    });
                  }
                }
              });
            }
          });
        }

        if (
          field.exclusiveTests.required ||
          (field.type === 'object' &&
            field.fields?.value?.exclusiveTests?.required)
        ) {
          if (prefix === '') {
            fields.push(key);
          } else {
            fields.push(prefix);
            fields.push(prefix + '.' + key);
          }
        }
      }
    });

    return fields;
  }

  return (
    <FormProvider {...innerFormMethods}>
      <form
        className={`${css['form']} ${className}`}
        onSubmit={handleSubmit(confirm, error)}
        autoComplete="off"
      >
        {!hideHeader &&
          (typeof header === 'function' ? (
            header()
          ) : (
            <div className={css['header']}>
              {backTo && (
                <LinkComponent
                  label="back"
                  icon={faLongArrowLeft}
                  linkTo={backTo}
                />
              )}
              <h2>
                {t(title)}
                {title ===
                  t(
                    'pages.livestock.animals_and_events.animals.types.breeding'
                  ) && (
                  <span className={css['info']}>
                    {' '}
                    (
                    {t(
                      'pages.livestock.animals_and_events.events.breeding.info'
                    )}
                    )
                  </span>
                )}
              </h2>
              {edit && (
                <Link
                  className={`${buttonCss['button']} ${buttonCss['primary']} ${css['edit']}`}
                  to={edit}
                >
                  {t('edit')}
                </Link>
              )}
            </div>
          ))}
        <div className={css['body']}>
          <Notices accessor={noticeAccessor || pascalCase(mode) + 'Form'}>
            {showErrorNotice ? (
              <Notice
                text={t('Form data was not saved!')}
                status="error"
                onDismiss={() => setShowErrorNotice(false)}
              />
            ) : null}
          </Notices>
          {!!(top && top.length) && (
            <div className={css['top']}>
              <div className={css['top_form']}>
                <div className={'flex flex-wrap gap-5'}>
                  {top.length === 0 ||
                  (!values && ['view', 'edit', 'step'].includes(mode)) ? (
                    <div className={css['loading']}>
                      <div>{t('Loading')}</div>
                      <Spinner visible={true} size="lg" />
                    </div>
                  ) : (
                    top.map((Child: FormComponent, index) => {
                      return (
                        <Child
                          key={`form_${index}`}
                          formValues={values}
                          required={requiredFields()}
                          loadingFragment={loadingFragment}
                          readonly={mode === 'view'}
                        />
                      );
                    })
                  )}
                </div>
                <div className={css['top_filter']}></div>
              </div>
            </div>
          )}
          <CheckEventForm isEvent={!!top}>
            <div
              className={cn(css['body'], {
                [`max-w-[${
                  children ? '420px' : '100%'
                }] w-full flex flex-col gap-3.5 p-5`]: !!top,
              })}
            >
              {body.length === 0 ||
              (!values && ['view', 'edit', 'step'].includes(mode)) ? (
                <div className={css['loading']}>
                  <div>{t('Loading')}</div>
                  <Spinner visible={true} size="lg" />
                </div>
              ) : (
                body.map((Child: FormComponent, index) => {
                  return (
                    <Child
                      key={`form_${index}`}
                      formValues={values}
                      required={requiredFields()}
                      loadingFragment={loadingFragment}
                      readonly={mode === 'view'}
                    />
                  );
                })
              )}
              {top && footer?.length > 0 && (
                <div className={'flex gap-2 animal-event-form-footer'}>
                  {footer.map((Child: FormComponent, index) => {
                    return (
                      <Child
                        key={`form_${index}`}
                        loadingFragment={loadingFragment}
                      />
                    );
                  })}
                </div>
              )}
            </div>
            {children && (
              <div className={'flex-1 animal_event_inner__list'}>
                {children}
              </div>
            )}
          </CheckEventForm>
        </div>
        {!top && footer?.length > 0 && (
          <div className={css['footer']}>
            {footer.map((Child: FormComponent, index) => {
              return (
                <Child
                  key={`form_${index}`}
                  loadingFragment={loadingFragment}
                />
              );
            })}
          </div>
        )}
      </form>
      {confirmationModal}
    </FormProvider>
  );
}

const CheckEventForm = ({
  isEvent,
  children,
}: PropsWithChildren<{ isEvent: boolean }>) => {
  return isEvent ? (
    <div className={'flex animal_event_inner'}>{children}</div>
  ) : (
    <>{children}</>
  );
};
